import Button, { ButtonType } from '../../../../../ui/Button/BlockButton';
import { useTheme } from '../../../../../../hooks/useTheme';
import { amountLabel, money, usePlanSelect } from '../usePlanSelect';
import styles from '../Fetch.module.css';
import type {
  FetchPetFormValues,
  FetchQuoteResult,
  FetchSelectInput,
} from '../types';

type PlanSelection = Omit<FetchSelectInput, 'cartId' | 'prospectPetId'>;

interface FetchPlanSelectProps {
  quote: FetchQuoteResult;
  petInfo: FetchPetFormValues | null;
  onSelect: (selection: PlanSelection, monthlyPrice?: number) => void;
  onEdit: () => void;
  loading?: boolean;
  isMobile?: boolean;
}

export function FetchPlanSelect({
  quote,
  petInfo,
  onSelect,
  onEdit,
  loading,
  isMobile,
}: FetchPlanSelectProps) {
  const theme = useTheme();
  const p = usePlanSelect(quote, petInfo);

  if (p.matrix.length === 0) {
    return (
      <div className={styles.offerForm}>
        <p className={styles.offerSub} style={theme.bodytextDefaultNone}>
          No plans available for this pet.
        </p>
      </div>
    );
  }

  const handleContinue = () => {
    if (loading || !p.canSubmit) return;
    const selection = p.buildSelection();
    if (selection) onSelect(selection, p.monthly);
  };

  return (
    <div
      className={styles.offerForm}
      style={{ fontFamily: theme.bodytextDefaultNone?.fontFamily }}
    >
      <div className={styles.coverageCard}>
        <span className={styles.coverageChip}>{p.petName}&apos;s Coverage</span>
        <p className={styles.coveragePlanName}>
          Fetch Accident &amp; Illness Coverage
        </p>
        <div className={styles.coveragePriceWrap}>
          <p className={styles.coveragePrice}>{money(p.monthly, 2)}/mo</p>
          <p className={styles.coverageFinePrint}>
            All monthly fees and taxes included
          </p>
        </div>
      </div>

      <p className={styles.coverageAdjustNote}>
        Adjust your payout, deductible and reimbursement rate without losing
        coverage.
      </p>

      <div className={styles.fields}>
        <label className={styles.fieldBox}>
          <span className={styles.fieldLabel}>Maximum annual payout</span>
          <select
            className={styles.fieldSelect}
            value={p.limitIdx}
            onChange={(e) => p.setLimit(Number(e.target.value))}
          >
            {p.matrix.map((m, i) => (
              <option key={i} value={i}>
                {amountLabel(m.amount)}
              </option>
            ))}
          </select>
        </label>

        <label className={styles.fieldBox}>
          <span className={styles.fieldLabel}>Deductible</span>
          <select
            className={styles.fieldSelect}
            value={p.dedIdx}
            onChange={(e) => p.setDed(Number(e.target.value))}
          >
            {(p.entry?.deductible ?? []).map((d, i) => (
              <option key={i} value={i}>
                {amountLabel(d.amount)}
              </option>
            ))}
          </select>
        </label>

        <label className={styles.fieldBox}>
          <span className={styles.fieldLabel}>Reimbursement</span>
          <select
            className={styles.fieldSelect}
            value={p.coPayIdx}
            onChange={(e) => p.setCoPay(Number(e.target.value))}
          >
            {(p.ded?.coPay ?? []).map((c, i) => (
              <option key={i} value={i}>
                {c.percentage}%
              </option>
            ))}
          </select>
        </label>
      </div>

      {petInfo && (
        <p className={styles.planSummary}>
          <span>
            Customized coverage for <strong>{p.summary}</strong> living in{' '}
            <strong>{petInfo.zip}</strong>.
          </span>
          <button
            type="button"
            className={styles.planSummaryEdit}
            onClick={onEdit}
          >
            Edit
          </button>
        </p>
      )}

      <div className={isMobile ? styles.stickyActions : styles.actions}>
        <Button
          type={ButtonType.button}
          onClick={handleContinue}
          big
          disabled={loading || !p.canSubmit}
        >
          {loading ? 'Setting up your plan…' : `Get ${p.petName} covered`}
        </Button>
      </div>
    </div>
  );
}
